import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Newspaper, Calendar, ArrowRight, User } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data || []);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="pt-20" data-testid="news-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              News & Updates
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
              Latest <span className="text-amber-700 italic">News</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
              Stay informed about our advocacy work, community events, and the latest developments in sickle cell disease care and research.
            </p>
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-16 bg-white" data-testid="news-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : posts.length === 0 ? (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-12 text-center">
                <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-6" />
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">
                  No News Yet
                </h3>
                <p className="text-slate-500 mb-6">
                  We're working on bringing you the latest updates about our advocacy work and community initiatives. Check back soon!
                </p>
                <Link to="/contact">
                  <Button className="bg-teal-700 hover:bg-teal-800 text-white">
                    Subscribe to Updates
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Card key={post.id} className="card-hover overflow-hidden" data-testid={`news-post-${post.id}`}>
                  {post.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.created_at)}
                      </span>
                      {post.author && (
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {post.author}
                        </span>
                      )}
                    </div>
                    <span className="inline-block bg-teal-100 text-teal-800 text-xs font-medium px-2 py-1 rounded mb-3">
                      {post.category}
                    </span>
                    <h3 className="font-serif text-xl font-semibold text-slate-900 mb-2 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                      {post.excerpt || post.content.substring(0, 150) + "..."}
                    </p>
                    <Link 
                      to={`/news/${post.id}`}
                      className="inline-flex items-center text-teal-700 font-medium text-sm hover:text-teal-800"
                    >
                      Read More
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Stay Connected
          </h2>
          <p className="text-slate-300 mb-6">
            Subscribe to our newsletter to receive the latest news and updates directly in your inbox.
          </p>
          <Link to="/">
            <Button className="bg-amber-700 hover:bg-amber-800 text-white font-bold uppercase tracking-wide text-sm px-8">
              Subscribe Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default NewsPage;
